// ====================================
// CHANNEL SYNC MANAGER
// ====================================
// Gestisce la sincronizzazione bidirezionale con Airbnb e AvaiBook

import { supabase } from './supabaseClient';
import { designSystem } from '../styles/design-system';

// Types per la sincronizzazione
export interface ExternalBooking {
  id: string;
  platform: 'airbnb' | 'avaibook';
  accommodationId: string;
  checkIn: string;
  checkOut: string;
  guestCount: number;
  guestName: string;
  guestEmail?: string;
  guestPhone?: string;
  totalPrice: number;
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed' | 'no_show';
  externalId: string;
  rawData?: any;
}

export interface AvailabilityBlock {
  accommodationId: string;
  date: string;
  available: boolean;
  price?: number;
  platform: 'direct' | 'airbnb' | 'avaibook';
  minimumStay?: number;
}

export interface SyncStatus {
  lastSync: string;
  platform: 'airbnb' | 'avaibook';
  status: 'success' | 'error' | 'pending';
  message?: string;
}

// ===== CONFIGURAZIONE SYNC =====
const SYNC_CONFIG = {
  airbnb: {
    syncInterval: 2 * 60 * 60 * 1000, // 2 ore (come da documentazione Airbnb)
    retryAttempts: 3,
    retryDelay: 5000,
  },
  avaibook: {
    syncInterval: 15 * 60 * 1000, // 15 minuti (XML è più veloce)
    retryAttempts: 3,
    retryDelay: 3000,
  }
};

// ===== CLASSE PRINCIPALE =====
export class ChannelSyncManager {
  private static instance: ChannelSyncManager;
  private syncInProgress: Map<string, boolean> = new Map();
  private lastSyncTimes: Map<string, Date> = new Map();

  public static getInstance(): ChannelSyncManager {
    if (!ChannelSyncManager.instance) {
      ChannelSyncManager.instance = new ChannelSyncManager();
    }
    return ChannelSyncManager.instance;
  }

  // ===== SINCRONIZZAZIONE PRINCIPALE =====
  
  /**
   * Sincronizza disponibilità e prenotazioni con tutti i canali
   */
  async syncAllChannels(accommodationIds?: string[]): Promise<SyncStatus[]> {
    const results: SyncStatus[] = [];
    
    try {
      // Sync Airbnb
      const airbnbResult = await this.syncAirbnb(accommodationIds);
      results.push(airbnbResult);
      
      // Sync AvaiBook
      const avaiBookResult = await this.syncAvaiBook(accommodationIds);
      results.push(avaiBookResult);
      
      // Aggiorna log di sincronizzazione
      await this.updateSyncLog(results);
      
      return results;
    } catch (error) {
      console.error('Errore sincronizzazione canali:', error);
      return [{
        lastSync: new Date().toISOString(),
        platform: 'airbnb',
        status: 'error',
        message: error instanceof Error ? error.message : 'Errore sincronizzazione'
      }];
    }
  }

  /**
   * Sincronizzazione con Airbnb
   */
  private async syncAirbnb(accommodationIds?: string[]): Promise<SyncStatus> {
    const platform = 'airbnb';
    
    if (this.syncInProgress.get(platform)) {
      return {
        lastSync: new Date().toISOString(),
        platform,
        status: 'pending',
        message: 'Sincronizzazione già in corso'
      };
    }

    try {
      this.syncInProgress.set(platform, true);
      
      // 1. Recupera prenotazioni da Airbnb
      const airbnbBookings = await this.fetchAirbnbBookings(accommodationIds);
      
      // 2. Aggiorna database locale
      await this.updateLocalBookings(airbnbBookings, platform);
      
      // 3. Invia disponibilità aggiornata ad Airbnb
      await this.pushAvailabilityToAirbnb(accommodationIds);
      
      // 4. Sincronizza prezzi
      await this.syncPricingToAirbnb(accommodationIds);
      
      this.lastSyncTimes.set(platform, new Date());
      
      return {
        lastSync: new Date().toISOString(),
        platform,
        status: 'success',
        message: `${airbnbBookings.length} prenotazioni sincronizzate`
      };
      
    } catch (error: any) {
      console.error('❌ Errore sync Airbnb:', error);
      
      let userMessage = 'Errore Airbnb sconosciuto';
      
      // Messaggi d'errore più informativi per l'utente
      if (error?.code === '22P02') {
        userMessage = 'Database non aggiornato. Chiedi al developer di eseguire: database-add-ical-columns.sql';
      } else if (error?.message?.includes('calendario Airbnb')) {
        userMessage = 'URL iCal non valido. Verifica configurazione Airbnb.';
      } else if (error?.message?.includes('CORS')) {
        userMessage = 'Problema connessione Airbnb. Riprova tra poco.';
      } else if (error instanceof Error) {
        userMessage = error.message;
      }
      
      return {
        lastSync: new Date().toISOString(),
        platform,
        status: 'error',
        message: userMessage
      };
    } finally {
      this.syncInProgress.set(platform, false);
    }
  }

  /**
   * Sincronizzazione con AvaiBook
   */
  private async syncAvaiBook(accommodationIds?: string[]): Promise<SyncStatus> {
    const platform = 'avaibook';
    
    if (this.syncInProgress.get(platform)) {
      return {
        lastSync: new Date().toISOString(),
        platform,
        status: 'pending',
        message: 'Sincronizzazione già in corso'
      };
    }

    try {
      this.syncInProgress.set(platform, true);
      
      // 1. Recupera prenotazioni da AvaiBook (XML più veloce)
      const avaiBookBookings = await this.fetchAvaiBookBookings(accommodationIds);
      
      // 2. Aggiorna database locale
      await this.updateLocalBookings(avaiBookBookings, platform);
      
      // 3. Invia disponibilità aggiornata ad AvaiBook
      await this.pushAvailabilityToAvaiBook(accommodationIds);
      
      this.lastSyncTimes.set(platform, new Date());
      
      return {
        lastSync: new Date().toISOString(),
        platform,
        status: 'success',
        message: `${avaiBookBookings.length} prenotazioni sincronizzate`
      };
      
    } catch (error) {
      console.error('Errore sync AvaiBook:', error);
      return {
        lastSync: new Date().toISOString(),
        platform,
        status: 'error',
        message: error instanceof Error ? error.message : 'Errore AvaiBook'
      };
    } finally {
      this.syncInProgress.set(platform, false);
    }
  }

  // ===== FUNZIONI AIRBNB REALI =====
  
  private async fetchAirbnbBookings(accommodationIds?: string[]): Promise<ExternalBooking[]> {
    try {
      // Airbnb usa principalmente iCal per la sincronizzazione calendario
      // Per una sincronizzazione completa, serve l'API Partner Hub
      
      const bookings: ExternalBooking[] = [];
      
      // Recupera gli alloggi con i loro iCal URLs di Airbnb (SOLO QUELLI ABILITATI)
      let query = supabase
        .from('accommodations')
        .select('id, airbnb_ical_url, airbnb_listing_id, title_it')
        .eq('sync_enabled', true) // SOLO alloggi con sync abilitato
        .not('airbnb_ical_url', 'is', null); // SOLO quelli con URL iCal configurato
        
      if (accommodationIds) {
        query = query.in('id', accommodationIds);
      }
      
      const { data: accommodations, error } = await query;
      if (error) throw error;

      console.log(`🏠 Sincronizzazione Airbnb per ${accommodations?.length || 0} alloggi abilitati:`);
      accommodations?.forEach(acc => {
        console.log(`  ✅ ${acc.title_it} (ID: ${acc.id}) - Listing: ${acc.airbnb_listing_id}`);
      });
      
      // Per ogni alloggio, scarica e parsa l'iCal
      for (const accommodation of accommodations || []) {
        if (accommodation.airbnb_ical_url) {
          try {
            const icalBookings = await this.parseAirbnbiCal(
              accommodation.airbnb_ical_url, 
              accommodation.id
            );
            bookings.push(...icalBookings);
          } catch (error) {
            console.error(`Errore parsing iCal per alloggio ${accommodation.id}:`, error);
          }
        }
      }
      
      return bookings;
    } catch (error) {
      console.error('Errore fetch Airbnb bookings:', error);
      return [];
    }
  }

  private async pushAvailabilityToAirbnb(accommodationIds?: string[]): Promise<void> {
    try {
      // Per aggiornare la disponibilità su Airbnb serve l'API Partner Hub
      // L'iCal è solo in lettura
      
      console.log('⚠️ Push availability to Airbnb requires Partner Hub API');
      console.log('📋 Current implementation: iCal sync (read-only)');
      console.log('🔧 To enable two-way sync, configure Airbnb Partner Hub API');
      
      // Recupera disponibilità dal database per logging
      let query = supabase
        .from('availability')
        .select('*')
        .gte('date', new Date().toISOString().split('T')[0]);
      
      if (accommodationIds) {
        query = query.in('accommodation_id', accommodationIds);
      }
      
      const { data: availability, error } = await query;
      if (error) throw error;
      
      console.log(`📊 ${availability?.length || 0} date da sincronizzare con Airbnb`);
      
      // TODO: Implementare con Airbnb Partner Hub API quando disponibile
      // const airbnbAPI = new AirbnbPartnerAPI(process.env.AIRBNB_CLIENT_ID);
      // await airbnbAPI.updateAvailability(accommodationIds, availability);
      
      // Aggiorna flag di sincronizzazione per tracking
      if (availability?.length) {
        await supabase
          .from('availability')
          .update({ last_sync_airbnb: new Date().toISOString() })
          .in('id', availability.map(a => a.id));
      }
      
    } catch (error) {
      console.error('⚠️ Errore push Airbnb availability (continuo comunque):', error);
      
      // Non bloccare tutto il sync se il push availability fallisce
      // Questo è spesso dovuto a problemi di schema database che possono essere risolti separatamente
      if ((error as any)?.code === '22P02') {
        console.warn('💡 Problema database schema - esegui script database-add-ical-columns.sql');
      }
    }
  }

  private async syncPricingToAirbnb(accommodationIds?: string[]): Promise<void> {
    try {
      console.log('⚠️ Push pricing to Airbnb requires Partner Hub API');
      
      // Recupera pricing dal database per logging
      let query = supabase
        .from('accommodations')
        .select('id, price_per_night');
      
      if (accommodationIds) {
        query = query.in('id', accommodationIds);
      }
      
      const { data: accommodations, error } = await query;
      if (error) throw error;
      
      console.log(`💰 ${accommodations?.length || 0} alloggi con prezzi da sincronizzare`);
      
      // TODO: Implementare con Airbnb Partner Hub API
      
    } catch (error) {
      console.error('Errore sync pricing Airbnb:', error);
      throw error;
    }
  }
  
  // Helper per parsing iCal di Airbnb
  private async parseAirbnbiCal(icalUrl: string, accommodationId: string): Promise<ExternalBooking[]> {
    try {
      // Usa proxy CORS per evitare blocchi Airbnb
      const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(icalUrl)}`;
      
      console.log('🔄 Fetching iCal via proxy:', proxyUrl);
      
      const response = await fetch(proxyUrl, {
        headers: {
          'User-Agent': 'Mozilla/5.0 (compatible; J-O-H Calendar Sync)',
          'Accept': 'text/calendar,text/plain,*/*'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const icalData = await response.text();
      const bookings: ExternalBooking[] = [];
      
      // Parse semplice dell'iCal (per un parser completo usare libreria ical.js)
      const lines = icalData.split('\n');
      let currentEvent: any = {};
      let inEvent = false;
      
      for (let line of lines) {
        line = line.trim();
        
        if (line === 'BEGIN:VEVENT') {
          inEvent = true;
          currentEvent = {};
        } else if (line === 'END:VEVENT' && inEvent) {
          // Processa l'evento completato
          if (currentEvent.dtstart && currentEvent.dtend && currentEvent.summary) {
            bookings.push({
              id: currentEvent.uid || `airbnb_${Date.now()}`,
              externalId: currentEvent.uid || `airbnb_${Date.now()}`,
              platform: 'airbnb',
              accommodationId: accommodationId,
              checkIn: this.parseICalDate(currentEvent.dtstart),
              checkOut: this.parseICalDate(currentEvent.dtend),
              guestName: this.extractGuestFromSummary(currentEvent.summary),
              guestCount: 1,
              status: 'confirmed',
              totalPrice: 0, // iCal non include prezzi
              rawData: currentEvent
            });
          }
          inEvent = false;
        } else if (inEvent) {
          // Parse proprietà dell'evento
          const [key, ...valueParts] = line.split(':');
          const value = valueParts.join(':');
          
          switch (key) {
            case 'UID':
              currentEvent.uid = value;
              break;
            case 'DTSTART':
            case 'DTSTART;VALUE=DATE':
              currentEvent.dtstart = value;
              break;
            case 'DTEND':
            case 'DTEND;VALUE=DATE':
              currentEvent.dtend = value;
              break;
            case 'SUMMARY':
              currentEvent.summary = value;
              break;
            case 'DESCRIPTION':
              currentEvent.description = value;
              break;
          }
        }
      }
      
      return bookings;
    } catch (error) {
      console.error('❌ Errore parsing iCal:', error);
      
      // Messaggio d'errore più specifico
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Impossibile scaricare calendario Airbnb. Verifica URL iCal.');
      }
      
      throw error;
    }
  }
  
  private parseICalDate(dateString: string): string {
    // Parse date YYYYMMDD o YYYYMMDDTHHMMSSZ
    if (dateString.length === 8) {
      // YYYYMMDD
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      return `${year}-${month}-${day}`;
    } else {
      // YYYYMMDDTHHMMSSZ
      const year = dateString.substring(0, 4);
      const month = dateString.substring(4, 6);
      const day = dateString.substring(6, 8);
      return `${year}-${month}-${day}`;
    }
  }
  
  private extractGuestFromSummary(summary: string): string {
    // Airbnb iCal ha formato tipo "Reserved - John Doe" o "Blocked"
    if (summary.includes('Reserved -')) {
      return summary.replace('Reserved -', '').trim();
    }
    return 'Airbnb Guest';
  }

  // ===== FUNZIONI AVAIBOOK REALI =====
  
  private async fetchAvaiBookBookings(accommodationIds?: string[]): Promise<ExternalBooking[]> {
    try {
      // AvaiBook usa API XML per sincronizzazione completa
      const bookings: ExternalBooking[] = [];
      
      // Recupera credenziali AvaiBook dal database o env
      const avaiBookApiKey = process.env.REACT_APP_AVAIBOOK_API_KEY;
      const avaiBookXmlEndpoint = process.env.REACT_APP_AVAIBOOK_XML_ENDPOINT;
      
      if (!avaiBookApiKey || !avaiBookXmlEndpoint) {
        console.warn('⚠️ AvaiBook API credentials not configured');
        return [];
      }
      
      // Recupera gli alloggi con i loro codici AvaiBook
      let query = supabase
        .from('accommodations')
        .select('id, avaibook_property_id, avaibook_xml_url');
        
      if (accommodationIds) {
        query = query.in('id', accommodationIds);
      }
      
      const { data: accommodations, error } = await query;
      if (error) throw error;
      
      // Per ogni alloggio, recupera prenotazioni da AvaiBook
      for (const accommodation of accommodations || []) {
        if (accommodation.avaibook_property_id) {
          try {
            const avaiBookBookings = await this.fetchAvaiBookBookingsForProperty(
              accommodation.avaibook_property_id,
              accommodation.id,
              avaiBookApiKey,
              avaiBookXmlEndpoint
            );
            bookings.push(...avaiBookBookings);
          } catch (error) {
            console.error(`Errore fetch AvaiBook per alloggio ${accommodation.id}:`, error);
          }
        }
      }
      
      return bookings;
    } catch (error) {
      console.error('Errore fetch AvaiBook bookings:', error);
      return [];
    }
  }
  
  private async fetchAvaiBookBookingsForProperty(
    propertyId: string,
    accommodationId: string,
    apiKey: string,
    xmlEndpoint: string
  ): Promise<ExternalBooking[]> {
    try {
      // Costruisci richiesta XML per AvaiBook
      const xmlRequest = `
        <?xml version="1.0" encoding="UTF-8"?>
        <request>
          <auth>
            <api_key>${apiKey}</api_key>
          </auth>
          <method>getBookings</method>
          <params>
            <property_id>${propertyId}</property_id>
            <date_from>${new Date().toISOString().split('T')[0]}</date_from>
            <date_to>${new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}</date_to>
          </params>
        </request>
      `;
      
      // Invia richiesta a AvaiBook
      const response = await fetch(xmlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/xml',
          'Accept': 'application/xml'
        },
        body: xmlRequest
      });
      
      if (!response.ok) {
        throw new Error(`AvaiBook API error: ${response.status}`);
      }
      
      const xmlResponse = await response.text();
      return this.parseAvaiBookXmlResponse(xmlResponse, accommodationId);
      
    } catch (error) {
      console.error('Errore fetch AvaiBook property bookings:', error);
      return [];
    }
  }
  
  private parseAvaiBookXmlResponse(xmlResponse: string, accommodationId: string): ExternalBooking[] {
    try {
      // Parse semplice XML (per parser completo usare DOMParser o libreria XML)
      const bookings: ExternalBooking[] = [];
      
      // Regex per estrarre prenotazioni dal XML
      const bookingRegex = /<booking[^>]*>(.*?)<\/booking>/gs;
      let match;
      
      while ((match = bookingRegex.exec(xmlResponse)) !== null) {
        const bookingXml = match[1];
        
        const booking: ExternalBooking = {
          id: this.extractXmlValue(bookingXml, 'booking_id') || `avaibook_${Date.now()}`,
          externalId: this.extractXmlValue(bookingXml, 'booking_id') || `avaibook_${Date.now()}`,
          platform: 'avaibook',
          accommodationId: accommodationId,
          checkIn: this.extractXmlValue(bookingXml, 'check_in') || '',
          checkOut: this.extractXmlValue(bookingXml, 'check_out') || '',
          guestName: this.extractXmlValue(bookingXml, 'guest_name') || 'AvaiBook Guest',
          guestEmail: this.extractXmlValue(bookingXml, 'guest_email') || '',
          guestCount: parseInt(this.extractXmlValue(bookingXml, 'guest_count') || '1'),
          totalPrice: parseFloat(this.extractXmlValue(bookingXml, 'total_amount') || '0'),
          status: this.mapAvaiBookStatus(this.extractXmlValue(bookingXml, 'status') || 'confirmed'),
          rawData: { xml: bookingXml }
        };
        
        if (booking.checkIn && booking.checkOut) {
          bookings.push(booking);
        }
      }
      
      return bookings;
    } catch (error) {
      console.error('Errore parsing AvaiBook XML:', error);
      return [];
    }
  }
  
  private extractXmlValue(xml: string, tagName: string): string | null {
    const regex = new RegExp(`<${tagName}[^>]*>(.*?)<\/${tagName}>`, 's');
    const match = xml.match(regex);
    return match ? match[1].trim() : null;
  }
  
  private mapAvaiBookStatus(avaiBookStatus: string): 'confirmed' | 'pending' | 'cancelled' | 'completed' | 'no_show' {
    const statusMap: { [key: string]: 'confirmed' | 'pending' | 'cancelled' | 'completed' | 'no_show' } = {
      'confirmed': 'confirmed',
      'pending': 'pending',
      'cancelled': 'cancelled',
      'completed': 'completed',
      'no_show': 'no_show'
    };
    return statusMap[avaiBookStatus] || 'confirmed';
  }

  private async pushAvailabilityToAvaiBook(accommodationIds?: string[]): Promise<void> {
    try {
      const avaiBookApiKey = process.env.REACT_APP_AVAIBOOK_API_KEY;
      const avaiBookXmlEndpoint = process.env.REACT_APP_AVAIBOOK_XML_ENDPOINT;
      
      if (!avaiBookApiKey || !avaiBookXmlEndpoint) {
        console.warn('⚠️ AvaiBook API credentials not configured');
        return;
      }
      
      // Recupera disponibilità dal database
      let query = supabase
        .from('availability')
        .select('*, accommodations(avaibook_property_id)')
        .gte('date', new Date().toISOString().split('T')[0]);
      
      if (accommodationIds) {
        query = query.in('accommodation_id', accommodationIds);
      }
      
      const { data: availability, error } = await query;
      if (error) throw error;
      
      // Raggruppa per property_id
      const availabilityByProperty: { [key: string]: any[] } = {};
      
      availability?.forEach(avail => {
        const propertyId = avail.accommodations?.avaibook_property_id;
        if (propertyId) {
          if (!availabilityByProperty[propertyId]) {
            availabilityByProperty[propertyId] = [];
          }
          availabilityByProperty[propertyId].push(avail);
        }
      });
      
      // Invia disponibilità per ogni proprietà
      for (const [propertyId, propertyAvailability] of Object.entries(availabilityByProperty)) {
        try {
          await this.pushAvailabilityToAvaiBookProperty(
            propertyId,
            propertyAvailability,
            avaiBookApiKey,
            avaiBookXmlEndpoint
          );
        } catch (error) {
          console.error(`Errore push availability per property ${propertyId}:`, error);
        }
      }
      
      console.log(`✅ Sincronizzata disponibilità per ${Object.keys(availabilityByProperty).length} proprietà AvaiBook`);
      
      // Aggiorna flag di sincronizzazione
      if (availability?.length) {
        await supabase
          .from('availability')
          .update({ last_sync_avaibook: new Date().toISOString() })
          .in('id', availability.map(a => a.id));
      }
        
    } catch (error) {
      console.error('Errore push AvaiBook availability:', error);
      throw error;
    }
  }
  
  private async pushAvailabilityToAvaiBookProperty(
    propertyId: string,
    availability: any[],
    apiKey: string,
    xmlEndpoint: string
  ): Promise<void> {
    try {
      // Costruisci XML per aggiornamento disponibilità
      const availabilityXml = availability.map(avail => `
        <date>
          <day>${avail.date}</day>
          <available>${avail.available ? 1 : 0}</available>
          <price>${avail.price_override || 0}</price>
          <min_stay>${avail.minimum_stay_override || 1}</min_stay>
        </date>
      `).join('');
      
      const xmlRequest = `
        <?xml version="1.0" encoding="UTF-8"?>
        <request>
          <auth>
            <api_key>${apiKey}</api_key>
          </auth>
          <method>updateAvailability</method>
          <params>
            <property_id>${propertyId}</property_id>
            <availability>
              ${availabilityXml}
            </availability>
          </params>
        </request>
      `;
      
      const response = await fetch(xmlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/xml',
          'Accept': 'application/xml'
        },
        body: xmlRequest
      });
      
      if (!response.ok) {
        throw new Error(`AvaiBook API error: ${response.status}`);
      }
      
      console.log(`✅ Aggiornata disponibilità AvaiBook per property ${propertyId}: ${availability.length} date`);
      
    } catch (error) {
      console.error(`Errore push AvaiBook property ${propertyId}:`, error);
      throw error;
    }
  }

  // ===== FUNZIONI COMUNI =====
  
  private async updateLocalBookings(bookings: ExternalBooking[], platform: 'airbnb' | 'avaibook'): Promise<void> {
    for (const booking of bookings) {
      try {
        // Inserisci o aggiorna nella tabella external_bookings
        const { error } = await supabase
          .from('external_bookings')
          .upsert({
            external_id: booking.externalId,
            platform: booking.platform,
            accommodation_id: booking.accommodationId,
            check_in: booking.checkIn,
            check_out: booking.checkOut,
            guest_name: booking.guestName,
            guest_email: booking.guestEmail || '',
            guest_phone: booking.guestPhone || '',
            guest_count: booking.guestCount || 1,
            total_amount: booking.totalPrice || 0,
            status: booking.status,
            raw_data: booking.rawData || {},
            last_sync_at: new Date().toISOString(),
            sync_status: 'synced'
          }, {
            onConflict: 'external_id,platform'
          });

        if (error) {
          console.error(`Errore inserimento booking ${booking.externalId}:`, error);
        } else {
          console.log(`✅ Sincronizzata prenotazione ${platform} ${booking.externalId}`);
        }

        // Blocca automaticamente le date nella tabella availability
        await this.blockAvailability(booking.accommodationId, booking.checkIn, booking.checkOut);

      } catch (error) {
        console.error(`Errore sync booking ${booking.externalId}:`, error);
      }
    }
  }

  private async blockAvailability(accommodationId: string, checkIn: string, checkOut: string): Promise<void> {
    const startDate = new Date(checkIn);
    const endDate = new Date(checkOut);
    
    // Blocca ogni giorno del soggiorno
    for (let date = new Date(startDate); date < endDate; date.setDate(date.getDate() + 1)) {
      const dateStr = date.toISOString().split('T')[0];
      
      await supabase
        .from('availability')
        .upsert({
          accommodation_id: accommodationId,
          date: dateStr,
          available: false
        });
    }
  }

  private async updateSyncLog(results: SyncStatus[]): Promise<void> {
    try {
      const { error } = await supabase
        .from('sync_logs')
        .insert(results.map(result => ({
          platform: result.platform,
          status: result.status,
          message: result.message,
          synced_at: result.lastSync
        })));
      
      if (error) throw error;
    } catch (error) {
      console.error('Errore aggiornamento log sync:', error);
    }
  }

  // ===== HOOK PRENOTAZIONI DIRETTE =====
  
  /**
   * Chiamata quando viene fatta una prenotazione diretta
   * Aggiorna immediatamente i canali esterni
   */
  async onDirectBookingCreated(accommodationId: string, checkIn: string, checkOut: string): Promise<void> {
    try {
      // Blocca immediatamente la disponibilità su tutti i canali
      await this.pushAvailabilityToAirbnb([accommodationId]);
      await this.pushAvailabilityToAvaiBook([accommodationId]);
      
      console.log('Disponibilità aggiornata su tutti i canali per prenotazione diretta');
    } catch (error) {
      console.error('Errore aggiornamento canali per prenotazione diretta:', error);
    }
  }

  // ===== UTILITY =====
  
  /**
   * Verifica se la sincronizzazione è necessaria
   */
  isSyncNeeded(platform: 'airbnb' | 'avaibook'): boolean {
    const lastSync = this.lastSyncTimes.get(platform);
    if (!lastSync) return true;
    
    const interval = SYNC_CONFIG[platform].syncInterval;
    return (Date.now() - lastSync.getTime()) >= interval;
  }

  /**
   * Ottieni stato sincronizzazione per dashboard admin
   */
  async getSyncStatus(): Promise<{ airbnb: SyncStatus; avaibook: SyncStatus }> {
    try {
      const { data: logs, error } = await supabase
        .from('sync_logs')
        .select('*')
        .order('synced_at', { ascending: false })
        .limit(2);
      
      if (error) throw error;
      
      const airbnbLog = logs?.find(log => log.platform === 'airbnb');
      const avaiBookLog = logs?.find(log => log.platform === 'avaibook');
      
      return {
        airbnb: airbnbLog || {
          lastSync: 'Mai sincronizzato',
          platform: 'airbnb',
          status: 'pending',
          message: 'Nessuna sincronizzazione trovata'
        },
        avaibook: avaiBookLog || {
          lastSync: 'Mai sincronizzato',
          platform: 'avaibook',
          status: 'pending',
          message: 'Nessuna sincronizzazione trovata'
        }
      };
    } catch (error) {
      console.error('Errore recupero stato sync:', error);
      return {
        airbnb: { lastSync: 'Errore', platform: 'airbnb', status: 'error' },
        avaibook: { lastSync: 'Errore', platform: 'avaibook', status: 'error' }
      };
    }
  }
}

// Export singleton instance
export const channelSyncManager = ChannelSyncManager.getInstance();

// ===== SCHEDULED SYNC =====
// Da utilizzare con cron job o scheduler

export const scheduleSync = () => {
  // Sync ogni 2 ore per Airbnb
  setInterval(async () => {
    if (channelSyncManager.isSyncNeeded('airbnb')) {
      await channelSyncManager.syncAllChannels();
    }
  }, SYNC_CONFIG.airbnb.syncInterval);
  
  // Sync ogni 15 minuti per AvaiBook
  setInterval(async () => {
    if (channelSyncManager.isSyncNeeded('avaibook')) {
      await channelSyncManager.syncAllChannels();
    }
  }, SYNC_CONFIG.avaibook.syncInterval);
}; 